import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect/MDSelect";
import useCreateDailyFormInspectionQuestion from "hooks/fleet/useCreateDailyFormInspectionQuestion";
import { DailyFormInspectionQuestionType } from "generated/graphql";

interface AddQuestionFormData {
  question: string;
  type: DailyFormInspectionQuestionType;
}

interface AddQuestionFormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

function AddQuestionForm({ onCancel, onSuccess }: AddQuestionFormProps) {
  const [createQuestion, { loading: creatingQuestion }] = useCreateDailyFormInspectionQuestion();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddQuestionFormData>({
    defaultValues: {
      question: "",
      type: DailyFormInspectionQuestionType.BOOLEAN,
    },
  });

  const handleSaveQuestion = async (data: AddQuestionFormData) => {
    await createQuestion({
      variables: {
        input: {
          question: data.question.trim(),
          type: data.type,
        },
      },
    });

    reset();
    onSuccess();
  };

  const handleCancelClick = () => {
    reset();
    onCancel();
  };

  return (
    <MDBox mb={3} p={2} border="1px solid #e0e0e0" borderRadius="8px">
      <MDTypography variant="h6" fontWeight="medium" mb={2}>
        Add New Question
      </MDTypography>
      <form onSubmit={handleSubmit(handleSaveQuestion)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              Question *
            </MDTypography>
            <Controller
              name="question"
              control={control}
              rules={{ required: "Question is required" }}
              render={({ field, fieldState: { error } }) => (
                <MDInput {...field} placeholder="Enter question text" fullWidth error={!!error} />
              )}
            />
            {errors.question && (
              <MDTypography variant="caption" color="error" mt={1}>
                {errors.question.message}
              </MDTypography>
            )}
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              Type *
            </MDTypography>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field, fieldState: { error } }) => (
                <MDSelect
                  {...field}
                  fullWidth
                  label="Type"
                  error={!!error}
                  options={[
                    { label: "True/False", value: DailyFormInspectionQuestionType.BOOLEAN },
                    { label: "Text", value: DailyFormInspectionQuestionType.TEXT },
                  ]}
                />
              )}
            />
            {errors.type && (
              <MDTypography variant="caption" color="error" mt={1}>
                {errors.type.message}
              </MDTypography>
            )}
          </Grid>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleCancelClick}
                disabled={creatingQuestion}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                type="submit"
                disabled={creatingQuestion}
              >
                {creatingQuestion ? "Saving..." : "Save"}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </form>
    </MDBox>
  );
}

export default AddQuestionForm;
